import { Box, Button, ButtonGroup, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { PopperWrapper } from "../Wrappers";

interface IFormFooterButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingButton?: boolean;
  splitButton?: boolean;
  splitButtonHasLoading?: boolean;
  startIcon?: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  shouldHide?: boolean;
  hasGap?: boolean;
  options?: IFormFooterButtonProps[];
  tooltip?: string;
}

interface IFormFooterButton extends IFormFooterButtonProps {
  optionalKey?: string;
  alignLeft?: boolean;
}

const FormSplitButton = (props: IFormFooterButtonProps) => {
  const { options, disabled, label, shouldHide, hasGap, splitButtonHasLoading, ...buttonProps } = props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="Button group with a nested menu"
        disabled={disabled}
        sx={{
          ...(shouldHide && { display: "none" }),
          ...(hasGap && { mr: 2 }),
        }}
      >
        <FormFooterButton label={label} key={label} loadingButton={splitButtonHasLoading} {...buttonProps} />
        <Button
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="More Options"
          aria-haspopup="menu"
          onClick={handleToggle}
          startIcon={buttonProps.startIcon}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <PopperWrapper
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        placement="top-start"
        zIndex={1500}
        popperProps={{
          open: open,
          sx: {
            width: 150,
          },
        }}
      >
        <Stack>
          {options?.map((button) => (
            <FormFooterButton key={button.label} hasGap={button.hasGap} {...button} />
          ))}
        </Stack>
      </PopperWrapper>
    </React.Fragment>
  );
};

const FormFooterButton = (props: IFormFooterButtonProps) => {
  const {
    label,
    onClick,
    disabled,
    isLoading,
    loadingButton,
    splitButton,
    variant,
    color,
    shouldHide,
    hasGap,
    options,
    splitButtonHasLoading,
    tooltip,
    startIcon,
  } = props;
  if (splitButton) {
    return (
      <FormSplitButton
        onClick={onClick}
        label={label}
        options={options}
        disabled={disabled}
        isLoading={isLoading}
        shouldHide={shouldHide}
        hasGap={hasGap}
        splitButtonHasLoading={splitButtonHasLoading}
      />
    );
  }
  if (loadingButton) {
    return (
      <Tooltip title={tooltip ?? ""} placement="top">
        <span>
          <Button
            id={`form-footer-loading-button-${label}`}
            variant={variant ?? "contained"}
            color={color ?? "primary"}
            onClick={onClick}
            loading={isLoading}
            loadingPosition="start"
            disabled={disabled}
            sx={{
              ...(shouldHide && { display: "none" }),
              ...(hasGap && { mr: 2 }),
            }}
            fullWidth
            startIcon={startIcon}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip ?? ""} placement="top">
      <span>
        <Button
          id={`form-footer-button-${label}`}
          type="submit"
          variant={variant}
          color={color ?? "primary"}
          disabled={disabled}
          onClick={onClick}
          sx={{
            ...(shouldHide && { display: "none" }),
            ...(hasGap && { mr: 2 }),
          }}
          startIcon={startIcon}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
};

interface IFormFooterProps {
  buttons: IFormFooterButton[];
  errorTexts?: string[];
  footerText?: string;
  outsideDrawer?: boolean;
}

export const FormFooter = (props: IFormFooterProps) => {
  const theme = useTheme();
  const { buttons, errorTexts, footerText, outsideDrawer } = props;

  const leftButtons = buttons.filter((btn) => btn.alignLeft);
  const rightButtons = buttons.filter((btn) => !btn.alignLeft);

  return (
    <Box
      sx={() => ({
        display: "flex",
        flexDirection: "row",
        py: 2,
        px: outsideDrawer ? 0 : 3,
        borderTop: outsideDrawer ? "none" : `1px solid ${theme.palette.divider}`,
        alignItems: "center",
      })}
    >
      {(errorTexts?.length ?? 0) > 0 && (
        <Stack
          sx={{
            flex: "1",
          }}
        >
          {errorTexts?.map((errorText) => (
            <Typography key={errorText} variant="body1" color={theme.palette.error.main}>
              {errorText}
            </Typography>
          ))}
        </Stack>
      )}
      {leftButtons
        .filter((btn) => btn.alignLeft)
        .map((button) => (
          <FormFooterButton key={button.optionalKey ?? button.label} hasGap={button.hasGap} {...button} />
        ))}
      <Box sx={{ flex: "1 1 auto" }} />
      {footerText && (
        <Typography variant="body1" sx={{ marginRight: 2 }}>
          {footerText}
        </Typography>
      )}
      {rightButtons
        .filter((btn) => !btn.alignLeft)
        .map((button) => (
          <FormFooterButton key={button.optionalKey ?? button.label} hasGap={button.hasGap} {...button} />
        ))}
    </Box>
  );
};
